import clsx from 'clsx'
import React from 'react'
import { GetStaticProps } from 'next'

import Seo from '@/molecules/SEO/SEO'
import SolsticeHero from '@/templates/Solstice/SolsticeHero'
import SolsticeStatistic from '@/templates/Solstice/SolsticeStatistic'
import SolsticeCard from '@/templates/Solstice/SolsticeCard'
import SolsticeSpeaker from '@/templates/Solstice/SolsticeSpeaker'
import SolsticeRegister from '@/templates/Solstice/SolsticeRegister'
import SolsticeFooter from '@/templates/Solstice/SolsticeFooter'
import SolsticeHeader from '@/templates/Solstice/SolsticeHeader'
import Link from '@/atoms/Link/Link'
import Button from '@/atoms/Button/Button'

/* utils */
import { getStrapiGraphqlData } from '@/utils/api/wrapper'
import { solsticeSpeakersQuery } from '@/utils/queries/solstice'
import { TEN_MINS_REVALIDATION } from '@/utils/constants/revalidation'

/* styling */
import styles from './_.module.css'

// assets
import { ReactComponent as Contest } from 'public/assets/solstice/images/Contest.svg'

import FiresideChat from 'public/assets/solstice/images/FiresideChat.svg'
import Keynotes from 'public/assets/solstice/images/Keynotes.svg'
import MasterClass from 'public/assets/solstice/images/MasterClass.svg'
import PanelDiscussion from 'public/assets/solstice/images/PanelDiscussion.svg'
import SolsticeOgImage from 'public/assets/solstice/images/solstice-og.jpg'
import NFTCard from 'public/assets/solstice/images/nft-card.webp'
import Image from 'next/legacy/image'
import SolsticeAgenda from '@/templates/Solstice/SolsticeAgenda'

type SolsticeCardType = {
  title?: string
  subTitle?: string
  content?: string
}

interface Props {
  speakers: SolsticeSpeaker[]
}
const solsticeCardData: SolsticeCardType[] = [
  {
    title: 'Unlock Your Creative Potential',
    content:
      'Learn and get inspired from global leaders, agency, and technology moguls as they spill the beans on what it takes to create great design that builds brands and legacies.'
  },
  {
    content:
      'A virtual creative summit bringing together the best in the creative space'
  },
  {
    content:
      'Join a community of 500,000+ creative professionals, leaders and marketeers'
  }
]

const headerMenu = [
  {
    title: 'Contest',
    href: '/solstice/contest',
    className: 'cursor-pointer'
  },
  {
    title: 'Speakers',
    href: '#speakers',
    className: 'cursor-pointer'
  },
  {
    title: 'Agenda',
    href: '#agenda',
    className: 'cursor-pointer'
  }
]

const metadata = [
  {
    property: 'og:title',
    content: `Virtual Design Marketing Summit | Pepper Content`
  },
  {
    property: 'description',
    content: `Creative Solstice is a one-of-a-kind summit gathering creative heads globally. The goal is to spark conversations and bring together people working in all areas of creativity.`
  },
  {
    property: 'og:description',
    content: `Creative Solstice is a one-of-a-kind summit gathering creative heads globally. The goal is to spark conversations and bring together people working in all areas of creativity.`
  },
  {
    property: 'og:image',
    content: SolsticeOgImage.src
  }
]

const SolsticePage = ({ speakers }: Props): JSX.Element => {
  return (
    <div className={clsx(styles.container, 'min-h-screen | text-white')}>
      <Seo
        title="Virtual Design Marketing Summit | Pepper Content"
        meta={metadata}
        canonical="/solstice/"
      />
      <div className={styles.image__container}>
        <SolsticeHeader className="mb-5" headerMenu={headerMenu} />
        <div className="max-width-container | lg:py-10 | px-10 lg:px-24 | backdrop-opacity-10 | lg:max-w-6xl xl:max-w-7xl | mx-auto | text-center">
          <SolsticeHero
            className="mb-5"
            title="A Virtual Design Marketing Summit"
            date="17th September, 2022"
            time="12 PM IST Onwards"
          />

          <Button type="secondary" size="default" className="mb-12 | lg:mb-24">
            <Link
              href={
                'https://www.airmeet.com/e/1c4fb4c0-29f7-11ed-bb22-3d2b6b56f545'
              }
            >
              Join Now
            </Link>
          </Button>

          <SolsticeStatistic className="mb-12 | max-w-4xl mx-auto" />

          <div className="mb-12">
            <iframe
              src="https://www.youtube.com/embed/hlmwmnghu90"
              title="Creative Solstice Contest | Participate and win INR 1 Lakh | Pepper Content"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              className={clsx('mx-auto', styles.iframe)}
            ></iframe>
          </div>

          <div className="grid-cols-1 md:grid-cols-2 md:gap-4 grid gap-4 mb-24">
            <div className="md:col-span-2 | max-w-4xl mx-auto">
              <div className="py-5 md:py-10 | px-4 md:px-8">
                <h2 className="h3 | font-bold text-center | mb-2">
                  {solsticeCardData[0]?.title}
                </h2>
                <h2 className="h3 | text-yellow-50 text-center font- | mb-2">
                  {solsticeCardData[0]?.subTitle}
                </h2>
                <p className="text-paragraph-4 text-center max-w-prose">
                  {solsticeCardData[0]?.content}
                </p>
              </div>
            </div>

            <SolsticeCard>
              <div className="w-py-5 md:py-10 | px-4 md:px-8">
                <h2 className="h4 | font-bold text-center | mb-2">
                  {solsticeCardData[1]?.title}
                </h2>
                <h2 className="h3 | text-yellow-50 text-center | mb-2">
                  {solsticeCardData[1]?.subTitle}
                </h2>
                <p className="text-paragraph-4 text-center">
                  {solsticeCardData[1]?.content}
                </p>
              </div>
            </SolsticeCard>

            <SolsticeCard>
              <div className="py-5 md:py-10 | md:px-8 px-4">
                <h2 className="h4 | font-bold text-center | mb-2">
                  {solsticeCardData[2]?.title}
                </h2>
                <h2 className="h3 | text-yellow-50 text-center | mb-2">
                  {solsticeCardData[2]?.subTitle}
                </h2>
                <p className="text-paragraph-4 text-center">
                  {solsticeCardData[2]?.content}
                </p>
              </div>
            </SolsticeCard>
          </div>

          <div className="mb-24">
            <h2 className="h3 | font-bold text-center | mb-12">
              What You’ll Experience
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 place-items-stretch">
              <div className={clsx(styles.exp__card, 'px-12 pt-12 ')}>
                <img src={FiresideChat} alt="" />
                <p className="h4 | font-bold | py-6">Fireside Chats</p>
              </div>
              <div className={clsx(styles.exp__card, 'px-12 pt-12')}>
                <img src={PanelDiscussion} alt="" className="h-full" />
                <p className="h4 | font-bold | py-6">Panel Discussions</p>
              </div>
              <div className={clsx(styles.exp__card, 'px-12')}>
                <img src={Keynotes} alt="" />
                <p className="h4 | font-bold | py-6">Keynotes</p>
              </div>
              <div className={clsx(styles.exp__card, 'px-12')}>
                <img src={MasterClass} alt="" />
                <p className="h4 | font-bold | py-6">Masterclasses</p>
              </div>
            </div>
          </div>
          <div className="max-w-lg mx-auto | text-center" id="contest">
            <Contest />
          </div>

          <div className="p-2 py-10 | text-center mb-4 lg:mb-4">
            An online visual content creation contest for all creative heads
            around the world!
          </div>

          <Link href="/solstice/contest">
            <p className="mb-8 | underline text-paragraph-1">
              Contest Guidelines
            </p>
          </Link>

          <div className="mb-12">
            <iframe
              src="https://www.youtube.com/embed/DWOSV8Hui20"
              title="Creative Solstice Contest | Participate and win INR 1 Lakh | Pepper Content"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              className={clsx('mx-auto', styles.iframe)}
            ></iframe>
          </div>

          <Button type="secondary" size="default" className="my-16">
            <Link href={'/solstice/participate'}> Entries are now closed</Link>
          </Button>

          <section className="py-16">
            <div className="grid grid-cols-1 md:grid-cols-2 items-center gap-16">
              <div className="max-w-[36rem] block">
                <Image
                  src={NFTCard.src}
                  alt="Image of a NFT card"
                  layout="responsive"
                  height={4620}
                  width={3648}
                />
              </div>
              <div className="details flex-col text-left">
                <h2 className="text-[2rem] md:text-h2 font-semibold">
                  Creative Solstice Attendance Token
                </h2>
                <h4 className="py-4 text-paragraph-1 font-bold">
                  HashCase Claimables
                </h4>
                <p className="text-paragraph-2 py-4 mb-6">
                  Awarded to all Pepper Content&apos;s Creative Solstice
                  Attendees, this NFT assures you entry to subsequent events and
                  can be used as a ticket for the same.
                </p>
                <a
                  href="https://www.hashcase.co/claim/localwallet/5"
                  className="block text-center py-6 px-4 w-full bg-white text-[#4015B3] rounded-lg text-paragraph-3 sm:text-paragraph-1 font-bold"
                >
                  Claim To HashCase Wallet
                </a>
              </div>
            </div>
          </section>
          <section>
            <SolsticeAgenda />
          </section>

          <div className="pb-16">
            <SolsticeSpeaker speakers={speakers} />
          </div>

          <SolsticeRegister className=" grid lg:grid-cols-12 | border-t-2 | pt-14 lg:pt-24 pb-18 | text-left" />

          <SolsticeFooter />
        </div>
      </div>
    </div>
  )
}

export const getStaticProps: GetStaticProps = async () => {
  const result = await getStrapiGraphqlData<SolsticePage>(
    solsticeSpeakersQuery.loc?.source.body
  )

  const speakers = result?.solsticeSpeakers?.data[0]
    ? result.solsticeSpeakers.data.map(item => ({
        name: item.attributes.name,
        company: item.attributes.company,
        designation: item.attributes.designation,
        image: item.attributes.image
      }))
    : []

  return {
    props: {
      speakers
    },
    revalidate: TEN_MINS_REVALIDATION
  }
}

export default SolsticePage
